import styled, { keyframes } from 'styled-components'

const cyanPulse = keyframes`
  0%, 100% { opacity: 0.8; }
  50% { opacity: 1; }
`

const arrowMove = keyframes`
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(4px); }
`

/* ===== Page Wrapper ===== */
export const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #02e5ff;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding-bottom: 80px; /* room for sticky bar */
`

/* ===== Hero Section ===== */
export const HeroSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px 20px 30px;
  text-align: center;
`

export const PageTitle = styled.h1`
  color: #fff;
  font-size: 3.5rem;
  font-weight: 900;
  margin: 0 0 15px 0;
  text-transform: uppercase;
  letter-spacing: 4px;
  font-style: italic;
  text-shadow:
    0 0 10px rgba(252, 10, 126, 0.8),
    0 0 20px rgba(252, 10, 126, 0.5),
    0 0 40px rgba(252, 10, 126, 0.3);

  @media (max-width: 600px) {
    font-size: 2rem;
  }
`

export const PageSubtitle = styled.p`
  color: #ccc;
  font-size: 0.95rem;
  margin: 0 0 22px 0;
  letter-spacing: 0.3px;
`

export const CallNowBtn = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #ff0066, #ff3399);
  color: #fff;
  padding: 13px 40px;
  border-radius: 50px;
  font-weight: 800;
  font-size: 1rem;
  text-transform: uppercase;
  text-decoration: none;
  letter-spacing: 2px;
  cursor: pointer;
  border: none;
  box-shadow: 0 0 20px rgba(255, 0, 102, 0.7), 0 0 40px rgba(255, 0, 102, 0.3);
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 30px rgba(255, 0, 102, 0.9), 0 0 60px rgba(255, 0, 102, 0.5);
  }
`

export const OrderOnlineText = styled.p`
  color: #888;
  font-size: 0.85rem;
  margin-top: 14px;
`

/* ===== Value Icon Section ===== */
export const SectionBlock = styled.div`
  padding: 10px 2.5% 30px;
`

export const SectionTitle = styled.h2`
  color: #fff;
  font-style: italic;
  font-weight: 900;
  font-size: 1.6rem;
  margin: 0 0 25px 0;
`

export const ValueIconGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

export const ValueIconCard = styled.div`
  border: 2px solid #fc0a7e;
  border-radius: 25px;
  padding: 10px 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  background: #02e5ff;
  box-shadow: 0 0 20px rgba(252, 10, 126, 0.4), inset 0 0 15px rgba(252, 10, 126, 0.1);
  transition: box-shadow 0.3s, transform 0.3s;
  cursor: default;
  min-height: 220px;

  &:hover {
    box-shadow: 0 0 30px rgba(252, 10, 126, 0.6), inset 0 0 20px rgba(252, 10, 126, 0.15);
    transform: translateY(-5px);
  }

  img {
    height: 90px;
    width: auto;
    object-fit: contain;
    filter: drop-shadow(0 0 10px rgba(252, 10, 126, 0.6));

    @media (max-width: 600px) {
      height: 80px;
    }
  }
`

export const ValueIconLabel = styled.span`
  color: #fff;
  font-size: 0.9rem;
  font-weight: 800;
  text-align: center;
  letter-spacing: 0.5px;
  font-style: italic;
`

/* ===== How It Works Steps Row ===== */
export const StepsBlock = styled.div`
  padding: 25px 2% 10px;
`

export const StepsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6%;
  margin-bottom: 25px;

  @media (max-width: 700px) {
    flex-wrap: wrap;
    gap: 10px;
  }
`

export const StepCircle = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 3px solid #00e5ff;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #02e5ff;
  box-shadow: 0 0 20px rgba(0, 229, 255, 0.5), inset 0 0 15px rgba(0, 229, 255, 0.2);
  animation: ${cyanPulse} 2s ease-in-out infinite;
  flex-shrink: 0;

  @media (max-width: 600px) {
    width: 80px;
    height: 80px;
  }

  img {
    width: 60px;
    height: 60px;
    object-fit: contain;

    @media (max-width: 600px) {
      width: 45px;
      height: 45px;
    }
  }
`

export const StepArrow = styled.div`
  display: flex;
  align-items: center;
  animation: ${arrowMove} 2s ease-in-out infinite;

  svg {
    width: 55px;
    height: 22px;
    overflow: visible;
  }

  @media (max-width: 600px) {
    display: none;
  }
`

export const HowItWorksLabel = styled.h2`
  color: #fff;
  font-style: italic;
  font-weight: 800;
  font-size: 1.3rem;
  margin: 0 0 20px 0;
  padding: 0 2.5%;
`

/* ===== Yellow Banner ===== */
export const YellowBanner = styled.div`
  width: 95%;
  margin: 0 auto 30px auto;
  background-color: #fff000;
  padding: 28px 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-radius: 14px;
  text-align: center;
`

export const BannerTitle = styled.h2`
  color: #02e5ff;
  font-weight: 900;
  font-size: 1.9rem;
  margin: 0 0 6px 0;
  font-style: italic;

  @media (max-width: 600px) {
    font-size: 1.3rem;
  }
`

export const BannerSubtitle = styled.p`
  color: #02e5ff;
  font-size: 0.88rem;
  margin: 0;
  font-weight: 500;
`

/* ===== FAQ Section ===== */
export const FAQSection = styled.div`
  padding: 10px 2.5% 30px;
`

export const FAQTitle = styled.h2`
  color: #fff;
  font-style: italic;
  font-weight: 800;
  font-size: 1.3rem;
  margin: 0 0 18px 0;
`

export const FAQGrid = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
`

export const FAQCard = styled.div`
  flex: 1;
  min-width: 140px;
  border: 1.5px solid #fc0a7e;
  border-radius: 10px;
  padding: 16px;
  background: transparent;
  box-shadow: 0 0 10px rgba(252, 10, 126, 0.2);
  transition: box-shadow 0.3s, transform 0.3s;
  cursor: pointer;

  &:hover {
    box-shadow: 0 0 18px rgba(252, 10, 126, 0.4);
    transform: translateY(-2px);
  }

  @media (max-width: 700px) {
    min-width: calc(50% - 8px);
    flex: none;
  }

  @media (max-width: 400px) {
    min-width: 100%;
  }
`

export const FAQCardTitle = styled.h3`
  color: #fc0a7e;
  font-size: 0.85rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  text-shadow: 0 0 8px rgba(252, 10, 126, 0.6);
`

export const FAQCardText = styled.p`
  color: #bbb;
  font-size: 0.78rem;
  margin: 0;
  line-height: 1.4;
`

export const StickyBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 64px;
  background-color: #0070ff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  box-shadow: 0 -4px 20px rgba(0, 112, 255, 0.5);
  z-index: 100;
`

export const StickyCallNow = styled.span`
  color: #fff;
  font-size: 1.1rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
`

export const StickyDialBtn = styled.a`
  background-color: #fff000;
  color: #02e5ff;
  padding: 10px 25px;
  border-radius: 6px;
  font-weight: 800;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.04);
  }
`

export const BottomBar = styled.div`
  width: 95%;
  margin: 0 auto 40px auto;
  background-color: #0056ff;
  padding: 10px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  border-radius: 50px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 15px;
    text-align: center;
    border-radius: 25px;
    padding: 20px;
  }
`

export const BottomText = styled.h2`
  color: #fff;
  font-style: italic;
  font-weight: bold;
  margin: 0;
  font-size: 1.5rem;
`

export const BottomBtn = styled.button`
  background-color: #fff000;
  color: #02e5ff;
  border: none;
  padding: 12px 30px;
  font-weight: bold;
  border-radius: 50px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`
